/**
 * ErrorBoundary — last line of defense against whole-app crashes.
 *
 * Without this, if a single component throws during render (e.g. a fetch
 * returns unexpected shape, a null gets dereferenced, a stale cache hits
 * a renamed field), the entire React tree unmounts and the user sees a
 * blank white screen with no way to recover.
 *
 * With this, the boundary catches the error, shows a friendly fallback,
 * and lets the user reload OR navigate home without restarting the app.
 *
 * Mounted at the top of App.tsx so it wraps every route. Sub-trees can
 * mount their own boundaries if they need finer-grained recovery (e.g.
 * inside ChatPanel, a chat error shouldn't unmount the whole dashboard).
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';
import { reloadFreshBypassingSW } from '../../lib/utils/force-fresh-reload';

/**
 * A failed dynamic import almost always means a STALE DEPLOY: the loaded
 * index.html references a chunk hash that no longer exists because a new build
 * shipped while the tab was open. Reloading fetches the fresh manifest and the
 * page works. Detect those by message (Vite/webpack/native variants).
 */
function isChunkLoadError(error: Error | null | undefined): boolean {
  const text = `${error?.name ?? ''} ${error?.message ?? ''}`;
  return /dynamically imported module|Loading chunk [\d]+ failed|ChunkLoadError|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically/i.test(text);
}

/** Reload once on a stale-chunk error, guarded against reload loops. */
function maybeReloadOnChunkError(error: Error | null | undefined): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;
  try {
    const KEY = 'wp:chunk-reload-at';
    const last = Number(window.sessionStorage.getItem(KEY) ?? '0');
    // If we already reloaded for this in the last 20s, don't loop — show the
    // fallback instead so the user isn't stuck refreshing forever.
    if (Number.isFinite(last) && Date.now() - last < 20_000) return false;
    window.sessionStorage.setItem(KEY, String(Date.now()));
    // Bypass the stale service worker (a plain reload gets re-served the stale
    // shell and the chunk 404s again → user stuck on "Try again").
    reloadFreshBypassingSW();
    return true;
  } catch {
    return false;
  }
}

/**
 * Fire-and-forget POST to the self-hosted error sink. Shared so the route-level
 * boundary reports too: RouteErrorBoundary previously caught render crashes and
 * dropped them on the floor, a silent hole that helped keep error_events empty.
 * Body matches ErrorIngestBody in apps/api/src/routes/observability.ts.
 */
function reportBoundaryError(boundary: string, error: Error, componentStack?: string): void {
  try {
    const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.waypoint-intel.com';
    void fetch(`${API_BASE}/v1/observability/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        source: 'client',
        level: 'error',
        name: (error.name || 'Error').slice(0, 200),
        message: (error.message || 'Unknown error').slice(0, 2000),
        stack: error.stack ? error.stack.slice(0, 5000) : undefined,
        url: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : undefined,
        context: {
          boundary,
          componentStack: componentStack ? componentStack.slice(0, 5000) : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
      }),
    })
      .then((res) => { if (!res.ok) console.warn(`[${boundary}] error report rejected: HTTP`, res.status); })
      .catch((e) => console.warn(`[${boundary}] error report failed`, e));
  } catch { /* never let reporting break the page */ }
}

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Stale-deploy chunk error → reload to the fresh build instead of showing
    // a scary fallback. Returns true (and navigates away) when it reloads.
    if (maybeReloadOnChunkError(error)) return;
    // Fire-and-forget POST to self-hosted error sink (no auth required).
    try {
      const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.waypoint-intel.com';
      void fetch(`${API_BASE}/v1/observability/errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Body must match the backend Zod schema (ErrorIngestBody in
        // apps/api/src/routes/observability.ts): source∈{client,server,cron,agent},
        // level∈{error,warning,fatal}, name required (≤200), message required
        // (≤2000), stack ≤5000, url ≤500. componentStack + userAgent ride
        // along in `context` (jsonb) — there is no top-level componentStack
        // column.
        body: JSON.stringify({
          source: 'client',
          level: 'error',
          name: (error.name || 'Error').slice(0, 200),
          message: (error.message || 'Unknown error').slice(0, 2000),
          stack: error.stack ? error.stack.slice(0, 5000) : undefined,
          url: typeof window !== 'undefined'
            ? window.location.href.slice(0, 500)
            : undefined,
          context: {
            componentStack: info.componentStack
              ? info.componentStack.slice(0, 5000)
              : null,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          },
        }),
        keepalive: true,
      })
        // A 400/500 RESPONSE doesn't reject a fetch — surface it too, so a
        // schema/ingest failure can't silently drain error_events.
        .then((res) => {
          if (!res.ok) console.warn('[ErrorBoundary] error report rejected: HTTP', res.status);
        })
        .catch((e) => console.warn('[ErrorBoundary] error report failed', e));
    } catch {
      // Swallow — error reporting must never throw
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

export class RouteErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('[RouteErrorBoundary]', error);
    // Stale-deploy chunk error (the common "Something went wrong loading this
    // page" + Try again, fixed by a manual refresh) → auto-reload instead; these
    // self-heal and are expected, so don't report them as product errors.
    if (isChunkLoadError(error)) { maybeReloadOnChunkError(error); return; }
    // Non-chunk route render crash — previously dropped silently. Report it so
    // it actually lands in error_events.
    reportBoundaryError('RouteErrorBoundary', error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <p className="text-ink-dim text-sm">Something went wrong loading this page.</p>
          <button
            className="px-4 py-2 bg-surface-2 text-ink rounded-lg text-sm hover:bg-surface-3 border border-border"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }): ReactNode {
  return (
    <div className="min-h-screen bg-bg-0 text-ink flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-border bg-surface-1 p-6 sm:p-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-danger/10 border border-danger/30 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wider text-danger">
          <span className="h-1.5 w-1.5 rounded-full bg-danger" />
          Something broke
        </div>
        <h1 className="text-2xl  mb-2">A panel hit an error.</h1>
        <p className="text-sm text-ink-muted leading-relaxed mb-4">
          We caught it before it crashed the whole app. Try again — most errors are
          transient (a sync just landed, a stale cache, a network blip). If it keeps
          happening, send the details below to{' '}
          <a href="mailto:support@waypoint-intel.com" className="text-primary hover:underline">
            support@waypoint-intel.com
          </a>.
        </p>

        <details className="mb-5 rounded-lg border border-border bg-bg-0 p-3">
          <summary className="cursor-pointer text-2xs font-semibold text-ink-muted">
            Technical details
          </summary>
          <pre className="mt-2 text-2xs text-ink-dim whitespace-pre-wrap font-mono overflow-auto max-h-40">
            {error.name}: {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </details>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={reset}
            className="rounded-md bg-primary text-white px-4 py-2 text-xs font-semibold hover:opacity-90 shadow-xs"
          >
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/dashboard'; }}
            className="rounded-md border border-border bg-bg-0 px-4 py-2 text-xs font-medium text-ink hover:bg-surface-2"
          >
            Go home
          </button>
          <button
            onClick={() => { reloadFreshBypassingSW(); }}
            className="rounded-md border border-border bg-bg-0 px-4 py-2 text-xs font-medium text-ink hover:bg-surface-2"
          >
            Hard reload
          </button>
        </div>
      </div>
    </div>
  );
}
